/** Tipos compartidos por el motor de cargas. */

import type { LocalDate } from '@cadencia/contracts';

export type SetType = 'effective' | 'top' | 'backoff' | 'drop' | 'rest_pause' | 'myo';

export type ProgressionStrategy =
  | 'double_progression'
  | 'load_progression'
  | 'rep_progression'
  | 'top_backoff'
  | 'rir_ramp';

export type WeekIntent = 'accumulation' | 'intensification' | 'deload' | 'reintroduction';

export interface RepTarget {
  repsMin: number;
  repsMax: number;
  rir: number;
}

export interface PerformedSetSummary {
  setIndex: number;
  loadKg: number;
  reps: number;
  rir: number;
  /** Objetivo con el que se prescribio esa serie, si se conoce. */
  target?: RepTarget;
}

export interface ExerciseSessionSummary {
  /** Instante de inicio de la sesion (ISO). */
  at: string;
  /**
   * El DIA de calendario en el que se entreno, en local (`YYYY-MM-DD`).
   *
   * Viaja aparte de `at` porque no se puede deducir de el aqui dentro: el
   * dominio no conoce el huso (ADR-007) y `at.slice(0, 10)` da el dia en UTC,
   * que entre medianoche y el desfase —en España, hasta las 02:00— es el
   * anterior. Entrenar de madrugada movia la sesion al dia de ayer y el
   * contador de estancamiento contaba mal, solo esas dos horas (NEW-001).
   *
   * Lo calcula quien construye el historial, que es quien si conoce el huso.
   */
  localDate: LocalDate;
  sets: PerformedSetSummary[];
}

export interface ExerciseHistory {
  /** De mas antigua a mas reciente. */
  sessions: ExerciseSessionSummary[];
}

export interface ProgressionState {
  strategy: ProgressionStrategy;
  currentE1rmKg: number | null;
  /** Sesiones consecutivas completando el tope del rango. */
  consecutiveTopRange: number;
  stallCounter: number;
  /** Sesgo aprendido de los overrides del usuario, acotado a +/-5 %. */
  overrideBiasPct: number;
}

/**
 * Lo que el motor de cargas LEE del readiness. Nada más.
 *
 * Tenía también `mean7d` y `mean28d`, y `BlockState` tenía `weekIndex`: tres
 * campos que el motor no leía en ninguna regla y que su único llamador rellenaba
 * a mano con `null`, `null` y `1` (NEW-5, 12/09/2026). No fallaba nada. El
 * peligro era la forma: parecían entradas, y la primera regla que se escribiera
 * sobre ellas habría leído un 1 constante sin error ninguno, que es exactamente
 * como empezó BUG-002 con `weekIntent`.
 *
 * Quitados y no conectados porque ninguna regla los pide: la tendencia del
 * readiness ya tiene su tipo, `ReadinessTrend`, y la usa la descarga; la rampa
 * de RIR del §4.5 llega escrita en el `targetRir` de cada prescripción. El día
 * que una regla del motor los necesite, se añaden con su productor delante.
 */
export interface ReadinessSummary {
  /** 0-100. `null` si hoy no hay check-in. */
  todayScore: number | null;
  /** Intensidad 1-5 de agujetas en las regiones que trabaja este ejercicio. */
  regionSoreness: number | null;
}

export interface BlockState {
  intent: WeekIntent;
}

export interface LoadRecommendation {
  /** Carga ya redondeada a una carga seleccionable. `null` = no se recomienda. */
  loadKg: number | null;
  rawLoadKg: number | null;
  confidence: number;
  reasonCodes: string[];
  /** Ajuste del objetivo de repeticiones cuando el incremento es grosero. */
  repsAdjustment: number;
  inputsSnapshot: Record<string, unknown>;
  engineVersion: string;
}

export const LOAD_ENGINE_VERSION = 'load_v1';
